import React, { useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { calcLayerThicknesses } from '../../lib/cadQuoteCalc';

/**
 * 2단계 "레이어로 나누기" 패널 - analysis.layerSuggest.candidates가 있을 때만
 * (단일 솔리드 파일) 뜬다. 체크박스로 추천/후보 레벨을 고르고, "높이 직접
 * 추가"로 임의 레벨도 넣을 수 있다. 실제 분할(불린 연산)은 서버가 하므로
 * 두께 미리보기만 cadQuoteCalc.calcLayerThicknesses로 화면에서 바로 계산한다.
 */
const CadQuoteLayerPanel = ({ layerSuggest, isLayered, currentSplitAt, onSplit, onCancel, loading }) => {
  const { axis, min, max, candidates } = layerSuggest;

  // 처음엔 추천(suggested) 레벨을, 이미 나눈 상태면 지금 적용된 splitAt을 기본
  // 체크 상태로 삼는다 - 화면을 봤을 때 "지금 뭐가 선택돼 있는지"가 바로 보이게.
  const defaultChecked = useMemo(() => {
    const base = isLayered && currentSplitAt?.length ? currentSplitAt : candidates.filter((c) => c.suggested).map((c) => c.level);
    return new Set(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [checkedLevels, setCheckedLevels] = useState(defaultChecked);
  const [customLevels, setCustomLevels] = useState([]);
  const [customInput, setCustomInput] = useState('');
  const [customError, setCustomError] = useState('');

  const selectedLevels = useMemo(
    () => [...checkedLevels, ...customLevels].sort((a, b) => b - a),
    [checkedLevels, customLevels],
  );
  const preview = useMemo(() => calcLayerThicknesses(selectedLevels, min, max), [selectedLevels, min, max]);

  const toggleLevel = (level) => {
    setCheckedLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  };

  const handleAddCustom = () => {
    const value = Number(customInput);
    if (!Number.isFinite(value)) {
      setCustomError('숫자를 입력해주세요.');
      return;
    }
    if (value <= min || value >= max) {
      setCustomError(`${min}~${max} 범위 안에서 입력해주세요.`);
      return;
    }
    if (checkedLevels.has(value) || customLevels.includes(value)) {
      setCustomError('이미 추가된 높이입니다.');
      return;
    }
    setCustomLevels((prev) => [...prev, value]);
    setCustomInput('');
    setCustomError('');
  };

  const handleRemoveCustom = (value) => {
    setCustomLevels((prev) => prev.filter((v) => v !== value));
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        레이어로 나누기 (두께 방향: {axis}축, {min}~{max}mm)
      </Typography>

      <Stack spacing={0.5} sx={{ mb: 1 }}>
        {candidates.map((c) => (
          <FormControlLabel
            key={c.level}
            control={<Checkbox size="small" checked={checkedLevels.has(c.level)} onChange={() => toggleLevel(c.level)} />}
            label={`${c.level}mm (면적 ${c.area}mm²)${c.suggested ? ' - 추천' : ''}`}
          />
        ))}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
        <TextField
          size="small"
          type="number"
          label="높이 직접 추가(mm)"
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          error={Boolean(customError)}
          helperText={customError || ' '}
          sx={{ maxWidth: 200 }}
        />
        <Button size="small" variant="outlined" onClick={handleAddCustom} sx={{ mt: 0.25 }}>추가</Button>
      </Stack>
      {customLevels.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
          {customLevels.map((v) => (
            <Button key={v} size="small" variant="text" onClick={() => handleRemoveCustom(v)}>
              {v}mm 제거 ×
            </Button>
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {preview.length > 1
          ? `레이어 ${preview.length}개: ${preview.map((p, i) => `#${i + 1} 두께 ${p.thickness.toFixed(2)}mm`).join(', ')}`
          : '분할 위치를 하나 이상 선택하세요.'}
      </Typography>

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          size="small"
          disabled={selectedLevels.length === 0 || loading}
          onClick={() => onSplit(selectedLevels, axis)}
          startIcon={loading ? <CircularProgress size={14} /> : null}
        >
          나눠서 다시 분석
        </Button>
        {isLayered && (
          <Button variant="outlined" size="small" onClick={onCancel} disabled={loading}>
            나누기 취소
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

export default CadQuoteLayerPanel;
